import { initializeApp } from "firebase/app";
import { initializeAnalytics, isSupported, logEvent } from "firebase/analytics";
import { getAuth } from "firebase/auth";
import { getFirestore } from "firebase/firestore"; // 🔥 Import Firestore

const firebaseConfig = {
  apiKey: import.meta.env.VITE_APP_APIKEY,
  authDomain: import.meta.env.VITE_APP_AUTHDOMAIN,
  databaseURL: import.meta.env.VITE_APP_DATABASEURL,
  projectId: import.meta.env.VITE_APP_PROJECTID,
  storageBucket: import.meta.env.VITE_APP_STORAGEBUCKET,
  messagingSenderId: import.meta.env.VITE_APP_MESSAGINGSENDERID,
  appId: import.meta.env.VITE_APP_APPID,
  measurementId: import.meta.env.VITE_APP_MEASUREMENTID,
};

// 🔥 Initialize Firebase
const firebaseApp = initializeApp(firebaseConfig);

// 🔥 Initialize Firestore
const db = getFirestore(firebaseApp);

// 🔥 Initialize Firebase Auth
const auth = getAuth(firebaseApp);

// 🔥 Initialize Firebase Analytics (if supported)
let analytics = null;

const analyticsReady = import.meta.env.VITE_APP_MEASUREMENTID
  ? isSupported()
      .then((supported) => {
        if (!supported) return null;
        analytics = initializeAnalytics(firebaseApp, {
          config: { send_page_view: false },
        });
        return analytics;
      })
      .catch((error) => {
        console.warn("Firebase Analytics is unavailable:", error);
        return null;
      })
  : Promise.resolve(null);

const logPageView = (pagePath) => analyticsReady.then((analyticsInstance) => {
  if (!analyticsInstance) return;
  logEvent(analyticsInstance, "page_view", {
    page_location: window.location.href,
    page_path: pagePath,
    page_title: document.title,
  });
});

const getPageType = () => {
  const { pathname } = window.location;
  if (pathname === "/") return "home";
  if (pathname === "/rentals") return "rentals_index";
  if (pathname.startsWith("/rentals/") || pathname.startsWith("/apply/")) return "rental_detail";
  if (pathname.startsWith("/pricing")) return "pricing";
  if (pathname.startsWith("/contact")) return "contact";
  if (pathname.startsWith("/login")) return "login";
  if (pathname.startsWith("/register")) return "register";
  if (pathname.includes("policy") || pathname.includes("terms") || pathname.includes("guidelines")) return "legal";
  return "app";
};

const cleanAnalyticsParams = (params = {}) => (
  Object.entries(params).reduce((acc, [key, value]) => {
    if (value === undefined || value === null || value === "") return acc;
    if (typeof value === "boolean" || typeof value === "number") {
      acc[key] = value;
      return acc;
    }
    acc[key] = String(value).slice(0, 100);
    return acc;
  }, {})
);

const logAnalyticsEvent = (eventName, params = {}) => analyticsReady.then((analyticsInstance) => {
  if (!analyticsInstance) return;
  logEvent(analyticsInstance, eventName, cleanAnalyticsParams({
    page_type: getPageType(),
    ...params,
  }));
}).catch((error) => {
  console.warn("Analytics event failed:", eventName, error);
});

export { firebaseApp, auth, db, analytics, logPageView, logAnalyticsEvent };
